import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Product, User, DosePurchaseEligibility } from '../../interfaces/product.interface';
import { Category } from '../../interfaces/category.interface';
import { ProductService } from '../../services/product.service';
import { CategoryService } from '../../services/category.service';
import { CartService } from '../../services/cart.service';
import { DoseColorService } from '../../services/dose-color.service';
import { AuthService } from '../../services/auth.service';
import { LanguageService } from '../../services/language.service';
import { ProductLocalizationService } from '../../services/product-localization.service';
import { CategoryLocalizationService } from '../../services/category-localization.service';
import { ToastService } from '../../services/toast.service';
import { ProductDetailSkeleton } from '../../components/shared/product-detail-skeleton/product-detail-skeleton';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-product-detail',
  imports: [CommonModule, ProductDetailSkeleton],
  templateUrl: './product-detail.html',
  styleUrl: './product-detail.scss'
})
export class ProductDetail implements OnInit, OnDestroy {
  product: Product | null = null;
  isLoadingProduct = true;
  selectedColorIndex = 0;
  selectedQuantity = 1;
  selectedQuantityOption = 30;
  cartCount = 0;
  currentUser: User | null = null;
  dosePurchaseEligibility: DosePurchaseEligibility | null = null;
  currentImageIndex = 0;
  intervalId: any;
  totalProducts :any = [];
  
  // Category selection
  categories: Category[] = [];
  selectedCategory: Category | null = null;
  selectedSubProductIndex: number = 0; // Track which sub-product option is selected
  isLoadingCategories = false;
  private destroy$ = new Subject<void>();

  /** Age (years) to recommended category prices. Used when user is logged in. */
  private static readonly AGE_RECOMMENDED_PRICES: { minAge: number; maxAge: number; prices: number[] }[] = [
    { minAge: 0, maxAge: 10, prices: [1500, 2500] },   // 0–10 (includes under 6 and future DOB treated as 0)
    { minAge: 11, maxAge: 20, prices: [3500] },
    { minAge: 21, maxAge: 25, prices: [4000, 4500] },
    { minAge: 26, maxAge: 99, prices: [5000] },
  ];

  private mockProducts: Product[] = [
  {
    id: '1',
    name: 'Dose One',
    description: 'Foundation Growth Formula',
    price: 1999.00,
    originalPrice: 2499.00,
    image: [
            './product/Dose-1Boxdesign.svg',
            './product/Pouch1ForPowder.svg',
            './product/Pouch-1backsideforPowder.png',
          ],
    doseType: 'green',
    doseNumber: 1,
    features: [
      'Foundation growth formula',
      'Initiates height growth process',
      'Natural ingredients',
      '30 days supply'
    ],
    benefits: [
      'Activates growth hormones',
      'Strengthens bone density',
      'Improves calcium absorption',
      'No side effects',
      'Clinically tested formula'
    ],
    ingredients: [
      'L-Arginine 200mg',
      'Calcium Carbonate 150mg',
      'Vitamin D3 100mg',
      'Zinc Oxide 50mg'
    ],
    usage: [
      "Take 2 spoons of powder from both pots only in the morning, and mix with the chocolate-flavored powder. Mix this with milk or water. (Cow’s milk gives better results.)",
      "If taken when the body is warm (e.g., after practice/workout), it shows better overall effects.",
      "Avoid eating anything for at least half an hour after taking the powder."
    ],
    inStock: true,
    isActive: true,
    badge: 'DOSE 1',
    comboPricing: {
      twoDoses: 3499.00, // Green + Yellow
      threeDoses: 4999.00 // All three doses
    }
  },
  {
    id: '2',
    name: 'Dose Two',
    description: 'Growth Enhancement Formula',
    price: 2199.00,
    originalPrice: 2699.00,
    image:  [
            './product/./product/Dose-2Boxdesign.svg',
            './product/Pouch1ForPowder.svg',
            './product/Pouch-1backsideforPowder.png',
          ],
    doseType: 'yellow',
    doseNumber: 2,
    features: [
      'Advanced growth enhancement',
      'Maximizes growth potential',
      'Strengthens growth plates',
      '30 days supply'
    ],
    benefits: [
      'Accelerates height growth',
      'Enhances muscle development',
      'Improves posture',
      'Boosts metabolism',
      'Increases energy levels'
    ],
    ingredients: [
      'Growth Hormone Peptides 250mg',
      'L-Ornithine 200mg',
      'Magnesium 100mg',
      'Vitamin B Complex 75mg'
    ],
    usage: [
      "Take 2 spoons of powder from both pots only in the morning, and mix with the chocolate-flavored powder. Mix this with milk or water. (Cow’s milk gives better results.)",
      "If taken when the body is warm (e.g., after practice/workout), it shows better overall effects.",
      "Avoid eating anything for at least half an hour after taking the powder."
    ],
    inStock: true,
    isActive: true,
    badge: 'DOSE 2',
    comboPricing: {
      twoDoses: 3899.00, // Yellow + Red
      threeDoses: 4999.00 // All three doses
    }
  },
  {
    id: '3',
    name: 'Dose Three',
    description: 'Maximum Growth Formula',
    price: 2399.00,
    originalPrice: 2999.00,
    image:  [
            './product/Dose-3Boxdesign.svg',
            './product/Pouch1ForPowder.svg',
            './product/Pouch-1backsideforPowder.png',
          ],
    doseType: 'red',
    doseNumber: 3,
    features: [
      'Maximum growth potential',
      'Final growth consolidation',
      'Long-lasting results',
      '30 days supply'
    ],
    benefits: [
      'Maximizes final height gain',
      'Consolidates growth results',
      'Strengthens bone structure',
      'Improves overall physique',
      'Permanent height increase'
    ],
    ingredients: [
      'Advanced Growth Complex 300mg',
      'Collagen Peptides 200mg',
      'L-Lysine 150mg',
      'Phosphorus 100mg'
    ],
    usage: [
      "Take 2 spoons of powder from both pots only in the morning, and mix with the chocolate-flavored powder. Mix this with milk or water. (Cow’s milk gives better results.)",
      "If taken when the body is warm (e.g., after practice/workout), it shows better overall effects.",
      "Avoid eating anything for at least half an hour after taking the powder."
    ],
    inStock: true,
    isActive: true,
    badge: 'DOSE 3'
  }
];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private categoryService: CategoryService,
    private cartService: CartService,
    private authService: AuthService,
    public doseColorService: DoseColorService,
    private languageService: LanguageService,
    private productLocalizationService: ProductLocalizationService,
    private categoryLocalizationService: CategoryLocalizationService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const productId = this.route.snapshot.paramMap.get('id');
    if (productId) {
      this.isLoadingProduct = true;
      this.productService.getProductById(productId).subscribe(product => {
        console.log('Product detail loaded product:', product);
        console.log('Product images:', product?.image);
        this.product = product || null;
        if (product) {
          console.log('Product has images, starting carousel');
          this.startCarousel();
          // Load categories for this product
          this.loadCategoriesForProduct(productId);
        }
        this.isLoadingProduct = false;
      });
    }

    // Get total product count from ProductService
    this.productService.getProducts().subscribe(products => {
      this.totalProducts = products;
    });

    this.cartService.getCartCount().subscribe(count => {
      this.cartCount = count;
    });

    // Load user data and dose eligibility
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) {
        this.authService.checkDosePurchaseEligibility(user.id).subscribe({
          next: (eligibility) => {
            this.dosePurchaseEligibility = eligibility;
          },
          error: (error) => {
            console.error('Error checking dose eligibility:', error);
          }
        });
      }
    });
  }


  goBack(): void {
    this.router.navigate(['/']);
  }

  increaseQuantity(): void {
    this.selectedQuantity++;
  }

  decreaseQuantity(): void {
    if (this.selectedQuantity > 1) {
      this.selectedQuantity--;
    }
  }

  selectQuantityOption(quantity: number): void {
    this.selectedQuantityOption = quantity;
  }

  buyNow(): void {
    if (this.product) {
      // Check if category is selected (if categories exist)
      if (this.categories.length > 0 && !this.selectedCategory) {
        const message = this.t('pleaseSelectCategory') || 'Please select a category first';
        this.toastService.warning(message);
        return;
      }

      // Check if sub-product option is selected (if category has sub-products)
      if (this.selectedCategory) {
        const subProducts = this.getSubProductsArray(this.selectedCategory);
        if (subProducts.length > 0 && (this.selectedSubProductIndex < 0 || this.selectedSubProductIndex >= subProducts.length)) {
          const message = this.t('pleaseSelectSubProduct');
          this.toastService.warning(message);
          return;
        }
      }

      // If user and eligibility loaded, enforce sequential purchase rule
      if (this.currentUser && this.dosePurchaseEligibility) {
        const doseNum = this.product.doseNumber;
        const eligibilityMap = this.dosePurchaseEligibility.canPurchaseDoses || {} as any;
        const isAllowed = (eligibilityMap[doseNum] !== undefined)
          ? !!eligibilityMap[doseNum]
          : (doseNum === 1 ? this.dosePurchaseEligibility.canPurchaseDose1
            : doseNum === 2 ? this.dosePurchaseEligibility.canPurchaseDose2
            : doseNum === 3 ? this.dosePurchaseEligibility.canPurchaseDose3
            : (this.dosePurchaseEligibility.completedDoses?.includes('green') &&
               this.dosePurchaseEligibility.completedDoses?.includes('yellow') &&
               this.dosePurchaseEligibility.completedDoses?.includes('red') &&
               !this.dosePurchaseEligibility.currentActiveDose));

        if (!isAllowed) {
          const msg = this.t('cannotPurchaseDose', { doseNumber: this.product.doseNumber.toString() });
          this.toastService.warning(msg);
          return;
        }
      }

      // Create a product copy with category info if category is selected
      const productToAdd = this.selectedCategory 
        ? { 
            ...this.product, 
            price: this.selectedCategory.price, 
            originalPrice: this.selectedCategory.originalPrice,
            selectedCategoryId: this.selectedCategory.id,
            selectedCategoryName: this.categoryLocalizationService.getLocalizedName(this.selectedCategory),
            selectedCategorySubProducts: this.getSubProductsArray(this.selectedCategory), // All sub-products for reference
            selectedSubProductIndex: this.selectedSubProductIndex, // Index of selected option
            selectedSubProductText: this.getSelectedSubProductText() // Actual text of selected option (for DB)
          }
        : this.product;

      // Check if item is already in cart (with same category if category is selected)
      const categoryId = this.selectedCategory?.id;
      if (this.cartService.isItemInCart(this.product.id, undefined, categoryId)) {
        // Show warning message if item is already in cart
        const message = this.t('itemAlreadyInCart', {
          productName: this.product.name
        });
        this.toastService.warning(message);
      } else {
        // Add to cart if not already present
        this.cartService.addToCart(productToAdd, this.selectedQuantity, undefined, categoryId);
        
        // Build success message with product name and category name
        const productName = this.getLocalizedName();
        const categoryName = this.selectedCategory 
          ? `(${this.categoryLocalizationService.getLocalizedName(this.selectedCategory)})`
          : '';
        const displayName = `${productName}${categoryName}`;
        
        // Show success message and redirect to cart
        const message = `Added 🌿 ${displayName} to cart!`;
        this.toastService.success(message);
        
        // Redirect to cart
        this.router.navigate(['/cart']);
      }
    }
  }

  startCarousel() {
    this.intervalId = setInterval(() => {
      const imageArray = this.getImageArray();
      if (imageArray.length > 1) {
        this.currentImageIndex = (this.currentImageIndex + 1) % imageArray.length;
      }
    }, 4000); // 4 seconds
  }

  goToImage(index: number) {
    this.currentImageIndex = index;
  }

  ngOnDestroy() {
    clearInterval(this.intervalId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load categories for the current product
  loadCategoriesForProduct(productId: string): void {
    this.isLoadingCategories = true;
    this.categoryService.getCategoriesByProductId(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categories) => {
          // Filter only active categories and sort by price (low to high)
          const priceNum = (c: Category) => Number(c.price) || 0;
          this.categories = categories
            .filter(cat => cat.isActive)
            .slice()
            .sort((a, b) => priceNum(a) - priceNum(b));
          console.log('Categories loaded for product:', this.categories);
          
          // Select first category by default with first sub-product option
          if (this.categories.length > 0) {
            this.selectCategory(this.categories[0]);
            this.selectedSubProductIndex = 0;
          }
          this.isLoadingCategories = false;
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          this.categories = [];
          this.isLoadingCategories = false;
        }
      });
  }

  // Select a category
  selectCategory(category: Category): void {
    this.selectedCategory = category;
    // Auto-select first sub-product option if available
    const subProducts = this.getSubProductsArray(category);
    this.selectedSubProductIndex = subProducts.length > 0 ? 0 : -1;
    console.log('Category selected:', category, 'Sub-product index:', this.selectedSubProductIndex);
  }

  // Select a specific sub-product option within a category
  selectSubProductOption(category: Category, subProductIndex: number, event?: Event): void {
    if (event) {
      event.stopPropagation(); // Prevent category card click
    }
    
    // If selecting a different category, switch to it first
    if (this.selectedCategory?.id !== category.id) {
      this.selectCategory(category);
    }
    
    this.selectedSubProductIndex = subProductIndex;
    console.log('Sub-product option selected:', subProductIndex, 'for category:', category.id);
  }

  // Get sub-products array for a category (with localization)
  getSubProductsArray(category: Category): string[] {
    return this.categoryLocalizationService.getLocalizedSubProducts(category);
  }

  // Check if a sub-product option is selected
  isSubProductOptionSelected(category: Category, subProductIndex: number): boolean {
    return this.selectedCategory?.id === category.id && this.selectedSubProductIndex === subProductIndex;
  }

  // Get selected sub-product text
  getSelectedSubProductText(): string {
    if (!this.selectedCategory) return '';
    const subProducts = this.getSubProductsArray(this.selectedCategory);
    if (subProducts.length > 0 && this.selectedSubProductIndex < subProducts.length) {
      return subProducts[this.selectedSubProductIndex];
    }
    return '';
  }

  // Get option label (Option A, Option B, etc.) - with localization
  getOptionLabel(index: number): string {
    const optionLetter = String.fromCharCode(65 + index);
    const optionKey = `option${optionLetter}`;
    const translated = this.t(optionKey);
    // If translation exists, use it, otherwise fallback to "Option A"
    if (translated !== optionKey) {
      return translated;
    }
    return `${this.t('option') || 'Option'} ${optionLetter}`;
  }

  // Check if category is selected
  isCategorySelected(category: Category): boolean {
    return this.selectedCategory?.id === category.id;
  }

  /** Parse dateOfBirth from Firestore (Timestamp, serialized, or Date). */
  private parseDateOfBirth(value: any): Date | null {
    if (value == null) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    const seconds = value?.seconds ?? value?._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  /** Get user age in years from dateOfBirth; null if not available. */
  getUserAge(): number | null {
    const dob = this.parseDateOfBirth(this.currentUser?.dateOfBirth);
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age < 0 ? 0 : age;
  }

  /** Recommended price(s) for current user's age; empty if not logged in or age out of range. */
  getRecommendedPricesForCurrentUser(): number[] {
    const age = this.getUserAge();
    if (age === null) return [];
    const row = ProductDetail.AGE_RECOMMENDED_PRICES.find(r => age >= r.minAge && age <= r.maxAge);
    return row ? row.prices : [];
  }

  /** True if user is logged in and this category's price is recommended for their age. */
  isCategoryRecommended(category: Category): boolean {
    if (!this.currentUser) return false;
    const recommended = this.getRecommendedPricesForCurrentUser();
    if (recommended.length === 0) return false;
    const categoryPrice = Math.round(Number(category.price) || 0);
    return recommended.some(p => Math.round(p) === categoryPrice);
  }

  // Get current price from selected category
  getCurrentPrice(): number {
    return this.selectedCategory?.price ?? this.product?.price ?? 0;
  }

  // Get original price from selected category
  getOriginalPrice(): number | undefined {
    return this.selectedCategory?.originalPrice ?? this.product?.originalPrice;
  }

  // Format sub-products for display (with localization)
  formatSubProducts(category: Category): string {
    return this.categoryLocalizationService.formatLocalizedSubProducts(category);
  }

  // Get localized category name
  getLocalizedCategoryName(category: Category): string {
    return this.categoryLocalizationService.getLocalizedName(category);
  }

  // Get dynamic dose array based on total products
  getDoseArray(): number[] {
    return Array.from({ length: this.totalProducts }, (_, i) => i + 1);
  }

  // Translation method
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  // Localized product name
  getLocalizedName(): string {
    return this.productLocalizationService.getLocalizedName(this.product);
  }

  // Localized product description
  getLocalizedDescription(): string {
    return this.productLocalizationService.getLocalizedDescription(this.product);
  }

  // Localized features
  getLocalizedFeatures(): string[] {
    return this.productLocalizationService.getLocalizedFeatures(this.product);
  }

  // Localized benefits
  getLocalizedBenefits(): string[] {
    return this.productLocalizationService.getLocalizedBenefits(this.product);
  }

  // Localized ingredients
  getLocalizedIngredients(): string[] {
    return this.productLocalizationService.getLocalizedIngredients(this.product);
  }

  // Localized usage
  getLocalizedUsage(): string[] {
    return this.productLocalizationService.getLocalizedUsage(this.product);
  }

  getDiscountPercentage(price: number, originalPrice: number = 0): number {
    if (!originalPrice || originalPrice <= 0) {
      return 0; // avoid division by zero
    }
    const discount = ((originalPrice - price) / originalPrice) * 100;
    return Math.round(discount); // nearest whole %
  }

  // Get proper image URL handling both Firebase Storage and local paths
  getImageUrl(imagePath: string): string {
    console.log('Product detail getImageUrl called with:', imagePath);
    console.log('Current product:', this.product);
    
    if (!imagePath || imagePath === '' || imagePath === null || imagePath === undefined) {
      console.log('No image path provided, using default');
      return '/assets/images/default-product.png';
    }
    
    // Firebase Storage URL (full URL)
    if (imagePath.startsWith('https://firebasestorage.googleapis.com/')) {
      console.log('Using Firebase Storage URL directly');
      return imagePath;
    }
    
    // Full URL or data URL
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
      console.log('Using full URL/data URL directly');
      return imagePath;
    }
    
    // Firebase Storage path (relative path like 'product-images/filename.jpg')
    if (imagePath.startsWith('product-images/')) {
      console.log('Firebase Storage path detected, but no direct URL conversion available');
      // For Firebase Storage paths, we need to construct the full URL
      // This is a limitation - in production you'd use Firebase Storage SDK to get the URL
      return '/assets/images/default-product.png'; // Fallback for now
    }
    
    // Local path - ensure it starts with ./
    if (imagePath.startsWith('./product/') || imagePath.startsWith('/product/')) {
      console.log('Using local product path');
      return imagePath.startsWith('./') ? imagePath : './' + imagePath.substring(1);
    }
    
    console.log('Unknown image path format, using default');
    return '/assets/images/default-product.png';
  }

  // Get image array handling both array and object formats
  getImageArray(): string[] {
    if (!this.product?.image) return [];
    
    // Handle both array and object formats
    const imageArray = Array.isArray(this.product.image) 
      ? this.product.image 
      : Object.values(this.product.image);
    
    // Filter out non-string values and ensure type safety
    return imageArray.filter((img): img is string => typeof img === 'string');
  }


  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      console.warn('Product detail image failed to load:', img.src);
      img.src = '/assets/images/default-product.png';
    }
  }
  
}
